import React from "react";
import EditUserModel from "../pages/users/EditUserModel";
import { AlertDialogs } from "../Dialgos/Alter-Daylogs";
import { useTranslation } from "react-i18next";
import { indexStore } from "src/store/indexStore";
import { Skeleton } from "../ui/skeleton";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import { User, Phone, Shield, CheckCircle2, XCircle, Edit, Trash2 } from "lucide-react";

export function UserCard(props) {
  const { t } = useTranslation();
  const { setUser, deleteUserTenantFunction } = indexStore();
  
  const editUser = (e) => {
    setUser(e);
  };
  
  const onDelete = (id) => {
    deleteUserTenantFunction(id);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900 dark:text-purple-200";
      case "data_manager":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200";
      case "call_center":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200";
    }
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-2 hover:border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-full border-2 bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
            </div>

            {/* Name and Role */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-foreground line-clamp-1 mb-2">
                {props.name || t("gloabl.unknown")}
              </h3>
              <Badge
                className={`w-fit text-xs font-semibold ${getRoleColor(props.role)}`}
              >
                <Shield className="w-3 h-3 mr-1" />
                {t(`userTypes.${props.role}`) || props.role}
              </Badge>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-1 shrink-0">
            <EditUserModel
              trigger={
                <button
                  onClick={() => editUser(props)}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                  title={t("gloabl.edit")}
                >
                  <Edit className="w-4 h-4 text-muted-foreground hover:text-primary" />
                </button>
              }
            />
            <AlertDialogs
              trigger={
                <button
                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  title={t("gloabl.delete")}
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              }
              variant="icon"
              onClickOk={() => onDelete(props.id)}
              description={<span>{t("gloabl.deleteMessage")}</span>}
              title={`${t("gloabl.delete")} ${t("gloabl.user")}`}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 pt-4 space-y-3">
        {/* Phone */}
        {props.phone && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Phone className="w-4 h-4" />
            <span>{t("gloabl.phone")}:</span>
            <span className="font-medium text-foreground">{props.phone}</span>
          </div>
        )}

        {/* Status */}
        <div className="border-t pt-3 mt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{t("gloabl.status")}:</span>
            </div>
            <Badge
              variant="outline"
              className={
                props.status
                  ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900 dark:text-green-200"
                  : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900 dark:text-red-200"
              }
            >
              {props.status ? (
                <>
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {t("gloabl.active")}
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3 mr-1" />
                  {t("gloabl.inactive")}
                </>
              )}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export const UserCardLoading = () => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-start gap-4">
        <Skeleton className="w-16 h-16 rounded-full shrink-0" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-5 w-24" />
          <div className="pt-3 border-t space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);
